import * as React from "react";
import { makeStyles } from "@material-ui/core";
import { find, nth } from "lodash";

import { colors, greys } from "@styles/color.styles";
import { fontFamily, fontFamilySans, weight } from "@styles/type.styles";

import { HeroStat } from "../hero-stat";
import { IOrgOverview } from "../overview.types";
import {
  formatDateAsShortMonthDate,
  formatNumberAsAbbreviation,
  getOrgAccountPeriodLength,
} from "../overview.util";
import { ZonedMoment } from "@clockwise/client-commons/src/util/time-zone";
import { ArrowUpward } from "@material-ui/icons";
import classNames from "classnames";

export interface HeroStatGridProps {
  orgOverview: IOrgOverview;
  startDate: string;
  endDate: string;
}

const useStyles = makeStyles({
  heroStatsWrapper: {
    margin: "28px 0px 32px",
    paddingBottom: 20,
    borderBottom: `solid 1px ${greys.borderGrey}`,
    display: "flex",
    flexWrap: "wrap",
  },
  container: {
    color: colors.blueDusk.darker,
    fontFamily,
    width: "49%",
    marginBottom: 20,
    "@media (min-width: 1030px)": {
      width: "25%",
      marginBottom: 0,
    },
  },
  hero: {
    fontSize: 40,
    lineHeight: 1.2,
    fontVariantNumeric: "proportional-nums lining-nums",
  },
  title: {
    fontWeight: weight.bold,
    fontSize: 12,
    lineHeight: "14px",
    textTransform: "uppercase",
    marginTop: 8,
  },
  subtitle: {
    fontFamily: fontFamilySans,
    fontWeight: weight.std,
    fontSize: 14,
    lineHeight: "16px",
    color: greys.slate,
    marginTop: 4,
  },
  percentChange__green: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    "& > svg": {
      fill: colors.green.darker,
      height: 14,
      width: 14,
    },
    "& > span": {
      fontWeight: weight.bold,
      color: colors.green.darker,
      margin: "0 4px 0 8px",
    },
  },
});

export const HeroStatGrid = ({ orgOverview, startDate, endDate }: HeroStatGridProps) => {
  const classes = useStyles();

  const { userCount } = orgOverview;
  const latestUserCount = userCount[userCount?.length - 1];

  const prevPeriodStartDate = new ZonedMoment(startDate)
    .clone()
    .subtract(getOrgAccountPeriodLength(orgOverview) || 14, "days")
    .format("YYYY-MM-DD");

  const getAttributeLabel = (attr: keyof IOrgOverview) => {
    const attributeData = orgOverview[attr];
    if (typeof attributeData !== "object") {
      return null;
    }

    return nth(attributeData, -2)?.label || nth(attributeData, -1)?.label || null;
  };

  const getAttributeStat = (attr: keyof IOrgOverview, date?: string) => {
    const attributeData = orgOverview[attr];
    if (typeof attributeData !== "object") {
      return null;
    }

    if (!startDate || !endDate) {
      return nth(attributeData, -2)?.value || nth(attributeData, -1)?.value || null;
    }

    return find(attributeData, { label: date || startDate })?.value || null;
  };

  const getAttributeChange = (attr: keyof IOrgOverview) => {
    const attributeData = orgOverview[attr];
    if (typeof attributeData !== "object" || !startDate || !endDate) {
      return null;
    }

    const endValue = getAttributeStat(attr);
    const startValue = getAttributeStat(attr, prevPeriodStartDate);

    if (!startValue || !endValue) {
      return null;
    }

    return Math.round(((endValue - startValue) * 100) / startValue);
  };

  const getAttributeSubtitle = (attr: keyof IOrgOverview) => {
    if (!startDate && !endDate && !getAttributeStat(attr)) {
      return <>None yet</>;
    }

    if (!startDate || !endDate) {
      const attributeDate = new ZonedMoment(getAttributeLabel(attr)).format("MMM DD");
      return <>As of {attributeDate}</>;
    }

    if (new ZonedMoment(endDate).subtract(1, "days").isSameOrAfter(new ZonedMoment())) {
      return <>During current period</>;
    }

    const percentChange = getAttributeChange(attr);
    if (!percentChange) {
      return <>During previous period</>;
    }

    const formattedPercentChange = formatNumberAsAbbreviation(percentChange);

    return (
      <div
        className={classNames({
          [classes.percentChange__green]: percentChange && percentChange > 0,
        })}
      >
        {percentChange > 0 && <ArrowUpward />}
        <span>
          {formattedPercentChange}
          %&nbsp;
        </span>
        vs. previous period
      </div>
    );
  };

  const maybeRenderCurrentUsers = () => {
    const subtitle = latestUserCount?.label
      ? `As of ${formatDateAsShortMonthDate(latestUserCount?.label)}`
      : "None yet";

    const formattedUserCount = latestUserCount?.value
      ? formatNumberAsAbbreviation(latestUserCount.value)
      : "-";

    return (
      <div className={classes.container}>
        <div className={classes.hero}>{formattedUserCount}</div>
        <div className={classes.title}>Users</div>
        <div className={classes.subtitle}>{subtitle}</div>
      </div>
    );
  };

  return (
    <div className={classes.heroStatsWrapper}>
      {maybeRenderCurrentUsers()}
      <HeroStat
        hero={getAttributeStat("scheduleAssistCount")}
        title={"Schedule assists"}
        subtitle={getAttributeSubtitle("scheduleAssistCount")}
      />
      <HeroStat
        hero={getAttributeStat("focusTimeHoursCreatedCount")}
        title={"Focus Time hours created"}
        subtitle={getAttributeSubtitle("focusTimeHoursCreatedCount")}
      />
      <HeroStat
        hero={getAttributeStat("conflictsResolvedCount")}
        title={"Conflicts resolved"}
        subtitle={getAttributeSubtitle("conflictsResolvedCount")}
      />
    </div>
  );
};
